import type React from 'react';
import { useCallback } from 'react';

import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { SEND_EMAILS } from 'config/graphql/queries';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';

import SendMessageDialog, { type SendMessageParams } from './SendMessageDialog';

interface SendEmailsDialogProps {
  isOpen?: boolean;
  tournamentId: string;
  participants: ParticipantInterface[];
  onClose: () => void;
  onExited?: () => void;
}

const SendEmailsDialog: React.FC<React.PropsWithChildren<SendEmailsDialogProps>> = ({ tournamentId, ...props }) => {
  const [sendEmails, { loading }] = useMutation(SEND_EMAILS);
  const { t } = useTranslation();

  const handleSendEmails = useCallback(
    async (params: SendMessageParams) => {
      const variables = {
        emailInput: {
          subject: params.subject,
          emailContent: params.message,
          copyToSender: params.copyToSender,
          copyToTournamentStaff: params.copyToTournamentStaff,
          replyEmail: params.replyTo,
        },
        personIds: params.personIds,
        tournamentId,
      };
      const res = await sendEmails({ variables });
      if (res) {
        const successMessages = res.data?.sendEmails?.some((contact) => contact.sentEmailAddresses?.length > 0);
        if (successMessages) toast.success(t('emailSendSuccess'));
      }
    },
    [sendEmails, tournamentId, t],
  );
  return (
    <SendMessageDialog
      {...props}
      withReplyTo
      withSendCopyOptions
      withSubject
      loading={loading}
      showParticipants={props.participants?.length > 1}
      title={t('sendEmail')}
      onSend={handleSendEmails}
    />
  );
};

export default SendEmailsDialog;
